import type { FolderResponse, LibraryItemResponse } from "@poseref/shared";
import {
  ASSET_TYPES,
  ASSETS_FOLDER,
  HIDDEN_FOLDER,
  REFERENCES_FOLDER,
} from "@poseref/shared";
import { create } from "zustand";
import * as libraryApi from "../api/library.ts";
import { useAuthStore } from "./authStore.ts";
import { ensureLibraryMaterialsLoaded } from "./materialLibraryCache.ts";
import type { RefImageMeta } from "./persistence/referenceImageDB.ts";
import {
  deleteReferenceImage,
  getAllReferenceImages,
  getReferenceImageUrl,
} from "./persistence/referenceImageDB.ts";

const DEFAULT_FOLDER_KEY = "poseref-default-folder";
const SORT_ORDER_KEY = "poseref-sort-order";

function getStoredValue(key: string): string | null {
  try {
    return typeof localStorage?.getItem === "function"
      ? localStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

function setStoredValue(key: string, value: string): void {
  try {
    if (typeof localStorage?.setItem === "function") {
      localStorage.setItem(key, value);
    }
  } catch {
    // Ignore persistence failures outside browser contexts.
  }
}

function removeStoredValue(key: string): void {
  try {
    if (typeof localStorage?.removeItem === "function") {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore persistence failures outside browser contexts.
  }
}

export type SortOrder = "modified" | "alphabetical" | "size";

interface LibraryState {
  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------
  items: LibraryItemResponse[];
  folders: FolderResponse[];
  localReferences: RefImageMeta[];
  /** Blob URLs for local reference thumbnails, keyed by hash */
  localReferenceThumbnailUrls: Map<string, string>;

  // ---------------------------------------------------------------------------
  // UI state
  // ---------------------------------------------------------------------------
  loading: boolean;
  error: string | null;
  selectedItems: Set<string>;
  selectedFolder: string | null; // null = All Items, ASSETS_FOLDER = assets view
  defaultFolder: string | null; // folder id to open on app load (null = All Items)
  searchQuery: string;
  sortOrder: SortOrder;

  // ---------------------------------------------------------------------------
  // Data actions
  // ---------------------------------------------------------------------------
  fetchItems: () => Promise<void>;
  fetchFolders: () => Promise<void>;
  fetchAll: () => Promise<void>;
  fetchLocalReferences: () => Promise<void>;
  deleteLocalReference: (hash: string) => Promise<void>;

  createItem: (
    data: Parameters<typeof libraryApi.createItem>[0],
  ) => Promise<LibraryItemResponse>;
  renameItem: (id: string, name: string) => Promise<void>;
  moveItems: (ids: string[], folderId: string | null) => Promise<void>;
  deleteItems: (ids: string[]) => Promise<void>;
  duplicateItems: (ids: string[]) => Promise<void>;
  hideItems: (ids: string[]) => Promise<void>;
  unhideItems: (ids: string[]) => Promise<void>;
  exportItems: (ids: string[]) => void;

  createFolder: (name: string) => Promise<void>;
  renameFolder: (id: string, name: string) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  setDefaultFolder: (id: string | null) => void;

  // ---------------------------------------------------------------------------
  // Selection / UI actions
  // ---------------------------------------------------------------------------
  setSelectedFolder: (id: string | null) => void;
  setSearchQuery: (q: string) => void;
  setSortOrder: (order: SortOrder) => void;
  toggleItemSelected: (id: string, additive: boolean) => void;
  selectRange: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
}

export const useLibraryStore = create<LibraryState>((set) => ({
  items: [],
  folders: [],
  localReferences: [],
  localReferenceThumbnailUrls: new Map(),
  loading: false,
  error: null,
  selectedItems: new Set(),
  selectedFolder: null,
  defaultFolder: null,
  searchQuery: "",
  sortOrder: (getStoredValue(SORT_ORDER_KEY) as SortOrder) || "modified",

  // ---------------------------------------------------------------------------
  // Data actions
  // ---------------------------------------------------------------------------

  fetchItems: async () => {
    set({ loading: true, error: null });
    try {
      const items = await libraryApi.fetchItems();
      set({ items, loading: false });
      // Eagerly warm the material JSON cache so gallery previews and
      // linked scene materials resolve synchronously.
      void ensureLibraryMaterialsLoaded(items);
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to load items",
      });
    }
  },

  fetchFolders: async () => {
    try {
      const folders = await libraryApi.fetchFolders();
      set({ folders });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load folders" });
    }
  },

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const [items, folders, cleanupResult] = await Promise.all([
        libraryApi.fetchItems(),
        libraryApi.fetchFolders(),
        libraryApi
          .cleanup()
          .catch(() => ({ migrated: 0, removed: 0, unfoldered: 0 })),
      ]);

      // Fire-and-forget: backfill file sizes from R2 in the background.
      libraryApi.backfillSizes().catch(() => {});

      // If cleanup changed anything, re-fetch so the list is accurate.
      let finalItems = items;
      if (
        cleanupResult.migrated > 0 ||
        cleanupResult.removed > 0 ||
        cleanupResult.unfoldered > 0
      ) {
        console.log(
          `[library] cleanup: migrated ${cleanupResult.migrated} blend(s), ` +
            `removed ${cleanupResult.removed} duplicate(s), ` +
            `unfoldered ${cleanupResult.unfoldered} asset(s)`,
        );
        finalItems = await libraryApi.fetchItems();
      }

      // Restore default folder preference on initial load
      const saved = getStoredValue(DEFAULT_FOLDER_KEY);
      const defaultFolder = saved ?? null;
      // Only apply if the folder still exists (or is a virtual folder)
      const validDefault =
        defaultFolder === null ||
        defaultFolder === ASSETS_FOLDER ||
        defaultFolder === REFERENCES_FOLDER ||
        defaultFolder === HIDDEN_FOLDER ||
        folders.some((f) => f.id === defaultFolder);
      const selectedFolder = validDefault ? defaultFolder : null;

      set({
        items: finalItems,
        folders,
        loading: false,
        defaultFolder,
        selectedFolder,
      });

      // Eagerly warm the material JSON cache so the gallery and linked
      // scene materials resolve without an extra round trip.
      void ensureLibraryMaterialsLoaded(finalItems);

      // Load local reference images from IndexedDB
      try {
        const localRefs = await getAllReferenceImages();
        // Revoke old thumbnail URLs
        const oldUrls = useLibraryStore.getState().localReferenceThumbnailUrls;
        for (const url of oldUrls.values()) URL.revokeObjectURL(url);
        // Create new thumbnail URLs
        const newUrls = new Map<string, string>();
        for (const ref of localRefs) {
          const url = await getReferenceImageUrl(ref.hash);
          if (url) newUrls.set(ref.hash, url);
        }
        set({
          localReferences: localRefs,
          localReferenceThumbnailUrls: newUrls,
        });
      } catch {
        // IndexedDB unavailable — leave empty
      }
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to load library",
      });
    }
  },

  fetchLocalReferences: async () => {
    try {
      const localRefs = await getAllReferenceImages();
      const oldUrls = useLibraryStore.getState().localReferenceThumbnailUrls;
      for (const url of oldUrls.values()) URL.revokeObjectURL(url);
      const newUrls = new Map<string, string>();
      for (const ref of localRefs) {
        const url = await getReferenceImageUrl(ref.hash);
        if (url) newUrls.set(ref.hash, url);
      }
      set({ localReferences: localRefs, localReferenceThumbnailUrls: newUrls });
    } catch {
      // IndexedDB unavailable
    }
  },

  deleteLocalReference: async (hash) => {
    await deleteReferenceImage(hash);
    set((s) => {
      const url = s.localReferenceThumbnailUrls.get(hash);
      if (url) URL.revokeObjectURL(url);
      const newUrls = new Map(s.localReferenceThumbnailUrls);
      newUrls.delete(hash);
      return {
        localReferences: s.localReferences.filter((r) => r.hash !== hash),
        localReferenceThumbnailUrls: newUrls,
      };
    });
  },

  createItem: async (data) => {
    const item = await libraryApi.createItem(data);
    set((s) => ({ items: [item, ...s.items] }));
    return item;
  },

  renameItem: async (id, name) => {
    await libraryApi.updateItem(id, { name });
    set((s) => ({
      items: s.items.map((i) =>
        i.id === id ? { ...i, name, updatedAt: new Date().toISOString() } : i,
      ),
    }));
  },

  moveItems: async (ids, folderId) => {
    await libraryApi.bulkAction({ action: "move", ids, folderId });
    set((s) => ({
      items: s.items.map((i) =>
        ids.includes(i.id)
          ? { ...i, folderId, updatedAt: new Date().toISOString() }
          : i,
      ),
      selectedItems: new Set(),
    }));
  },

  deleteItems: async (ids) => {
    // For any material items being deleted, cancel pending R2 uploads and
    // break any live links from scene materials — otherwise a debounced
    // flush would resurrect the row server-side, and linked scene blocks
    // would keep a dangling libraryId reference. The scene store is
    // notified via the shared stripMaterialLinks helper to avoid a
    // circular import between libraryStore and store.
    const state = useLibraryStore.getState();
    const materialIds: string[] = [];
    for (const id of ids) {
      const item = state.items.find((i) => i.id === id);
      if (item?.type === "material") materialIds.push(id);
    }
    // (no libraryId links to break — library materials are an immutable source)

    await libraryApi.bulkAction({ action: "delete", ids });
    set((s) => ({
      items: s.items.filter((i) => !ids.includes(i.id)),
      selectedItems: new Set(),
    }));
    useAuthStore.getState().refreshStorageUsage();
  },

  hideItems: async (ids) => {
    await libraryApi.bulkAction({ action: "hide", ids });
    set((s) => ({
      items: s.items.map((i) =>
        ids.includes(i.id)
          ? { ...i, hidden: true, updatedAt: new Date().toISOString() }
          : i,
      ),
      selectedItems: new Set(),
    }));
  },

  unhideItems: async (ids) => {
    await libraryApi.bulkAction({ action: "unhide", ids });
    set((s) => ({
      items: s.items.map((i) =>
        ids.includes(i.id)
          ? { ...i, hidden: false, updatedAt: new Date().toISOString() }
          : i,
      ),
      selectedItems: new Set(),
    }));
  },

  duplicateItems: async (ids) => {
    const newItems = await libraryApi.bulkDuplicate(ids);
    set((s) => ({
      items: [...newItems, ...s.items],
      selectedItems: new Set(newItems.map((i) => i.id)),
    }));
    useAuthStore.getState().refreshStorageUsage();
  },

  exportItems: (ids) => {
    const { items } = useLibraryStore.getState();
    for (const id of ids) {
      const item = items.find((i) => i.id === id);
      if (!item?.fileUrl) continue;
      const a = document.createElement("a");
      a.href = item.fileUrl;
      a.download = item.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  },

  createFolder: async (name) => {
    const folder = await libraryApi.createFolder(name);
    set((s) => ({ folders: [...s.folders, folder] }));
  },

  renameFolder: async (id, name) => {
    await libraryApi.updateFolder(id, { name });
    set((s) => ({
      folders: s.folders.map((f) => (f.id === id ? { ...f, name } : f)),
    }));
  },

  deleteFolder: async (id) => {
    await libraryApi.deleteFolder(id);
    set((s) => {
      const wasDefault = s.defaultFolder === id;
      if (wasDefault) removeStoredValue(DEFAULT_FOLDER_KEY);
      return {
        folders: s.folders.filter((f) => f.id !== id),
        items: s.items.map((i) =>
          i.folderId === id ? { ...i, folderId: null } : i,
        ),
        selectedFolder: s.selectedFolder === id ? null : s.selectedFolder,
        defaultFolder: wasDefault ? null : s.defaultFolder,
      };
    });
  },

  setDefaultFolder: (id) => {
    if (id === null) {
      removeStoredValue(DEFAULT_FOLDER_KEY);
    } else {
      setStoredValue(DEFAULT_FOLDER_KEY, id);
    }
    set({ defaultFolder: id });
  },

  // ---------------------------------------------------------------------------
  // Selection / UI
  // ---------------------------------------------------------------------------

  setSelectedFolder: (id) =>
    set({ selectedFolder: id, selectedItems: new Set() }),

  setSearchQuery: (searchQuery) => set({ searchQuery }),

  setSortOrder: (sortOrder) => {
    setStoredValue(SORT_ORDER_KEY, sortOrder);
    set({ sortOrder });
  },

  toggleItemSelected: (id, additive) => {
    set((s) => {
      const next = new Set(additive ? s.selectedItems : []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedItems: next };
    });
  },

  selectRange: (id) => {
    set((s) => {
      const visible = filteredItems(s);
      const lastSelected = [...s.selectedItems].pop();
      if (!lastSelected) return { selectedItems: new Set([id]) };

      const fromIdx = visible.findIndex((i) => i.id === lastSelected);
      const toIdx = visible.findIndex((i) => i.id === id);
      if (fromIdx === -1 || toIdx === -1)
        return { selectedItems: new Set([id]) };

      const [lo, hi] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
      const range = visible.slice(lo, hi + 1).map((i) => i.id);
      return { selectedItems: new Set([...s.selectedItems, ...range]) };
    });
  },

  selectAll: () => {
    set((s) => {
      const visible = filteredItems(s);
      return { selectedItems: new Set(visible.map((i) => i.id)) };
    });
  },

  clearSelection: () => set({ selectedItems: new Set() }),
}));

// ---------------------------------------------------------------------------
// Derived data helpers (pure functions, no store dependency)
// ---------------------------------------------------------------------------

/** Convert local reference images to LibraryItemResponse shape for display. */
function localRefsAsItems(
  state: Pick<LibraryState, "localReferences" | "localReferenceThumbnailUrls">,
): LibraryItemResponse[] {
  return state.localReferences.map((ref) => ({
    id: `local:${ref.hash}`,
    folderId: null,
    type: "reference_image" as const,
    name: ref.name,
    thumbnailUrl: state.localReferenceThumbnailUrls.get(ref.hash) ?? null,
    fileUrl: null,
    sourceFileUrl: null,
    sourceHash: ref.hash,
    hidden: false,
    fileSize: ref.size,
    metadata: null,
    createdAt: new Date(ref.createdAt).toISOString(),
    updatedAt: new Date(ref.createdAt).toISOString(),
  }));
}

/** Filter items by the current folder/view, search query, and sort order. */
export function filteredItems(
  state: Pick<
    LibraryState,
    | "items"
    | "selectedFolder"
    | "searchQuery"
    | "sortOrder"
    | "localReferences"
    | "localReferenceThumbnailUrls"
  >,
): LibraryItemResponse[] {
  let result = state.items;

  // --- View / folder filter ---
  if (state.selectedFolder === HIDDEN_FOLDER) {
    // Hidden view: only hidden non-asset items
    result = result.filter((i) => i.hidden && !ASSET_TYPES.has(i.type));
  } else if (state.selectedFolder === REFERENCES_FOLDER) {
    // References view: show local reference images from IndexedDB
    result = localRefsAsItems(state);
  } else if (state.selectedFolder === ASSETS_FOLDER) {
    // Assets view: only asset types, regardless of folderId, exclude hidden
    result = result.filter((i) => ASSET_TYPES.has(i.type) && !i.hidden);
  } else if (state.selectedFolder) {
    // User folder: items in that folder (any type), exclude hidden
    result = result.filter(
      (i) => i.folderId === state.selectedFolder && !i.hidden,
    );
  } else {
    // All Items: everything EXCEPT asset types, reference images, and hidden items
    result = result.filter(
      (i) =>
        !ASSET_TYPES.has(i.type) && i.type !== "reference_image" && !i.hidden,
    );
  }

  // --- Search filter ---
  if (state.searchQuery) {
    const q = state.searchQuery.toLowerCase();
    result = result.filter((i) => i.name.toLowerCase().includes(q));
  }

  // --- Sort ---
  const sortOrder = state.sortOrder ?? "modified";
  if (sortOrder === "modified") {
    result = result
      .slice()
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
  } else if (sortOrder === "alphabetical") {
    result = result
      .slice()
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
  } else if (sortOrder === "size") {
    result = result.slice().sort((a, b) => b.fileSize - a.fileSize);
  }

  return result;
}

/** Check whether a library item type is an asset (vs a scene). */
export function isAssetType(type: string): boolean {
  return ASSET_TYPES.has(type as any);
}

// ---------------------------------------------------------------------------
